import { Box, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import { shouldShowModuleSelector } from "../../_DataEndpointConfig/moduleSelector";
import type ChartCardStore from "../ChartCardStore";
import { getControlModules } from "./controlModules";
import { getChartSelectionFields } from "./selectionFields";

/**
 * Read-only copy of the chart's selections, shown only in the PNG export in place of the
 * dropdowns. It is hidden on screen; the export reveals every `.export-only` element while it
 * captures.
 */
const ExportSelectionFields = observer(
    ({ store }: { store: ChartCardStore }) => {
        const modules = getControlModules(store);
        const isModuleSelectorHidden =
            store.selectionType === "Module" &&
            !shouldShowModuleSelector(store.config.moduleSelector, modules);

        const fields = getChartSelectionFields({
            selectionType: store.selectionType,
            selectedIds: store.selectedIds,
            // A hidden module selector leaves the module out of the export, but other fields stay.
            modules: isModuleSelectorHidden ? [] : modules,
            llmClassifiers: store.optionsStore.llmClassifiers,
            hasDataGrid: !!store.config.dataGrid,
            skillGroupingLabel: store.config.skillGroupingSelector?.options.find(
                (option) => option.value === store.selectedSkillGrouping,
            )?.label,
        });
        if (!fields.length) return null;

        return (
            <Box
                className="export-only"
                sx={{
                    display: "none",
                    width: "100%",
                    marginBottom: theme.spacing(2),
                }}
            >
                {fields.map((field) => (
                    <Box
                        key={field.label}
                        sx={{ marginBottom: theme.spacing(1.5) }}
                    >
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: theme.palette.gray[600],
                                marginBottom: theme.spacing(0.5),
                            }}
                        >
                            {field.label}
                        </Typography>
                        <Box
                            sx={{
                                width:
                                    field.values.length > 1
                                        ? "100%"
                                        : "fit-content",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                border: `1px solid ${theme.palette.gray[200]}`,
                                borderRadius: `${theme.shape.borderRadius}px`,
                                backgroundColor: theme.palette.gray[50],
                                paddingInline: theme.spacing(1.5),
                                paddingBlock: theme.spacing(1),
                                fontSize: 14,
                                lineHeight: "22px",
                                color: theme.palette.gray[800],
                            }}
                        >
                            {field.values.join(", ")}
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    },
);

export default ExportSelectionFields;
